/**
 * useClickOutsideHandlers - Hook for managing click-outside event handlers
 *
 * Extracted from CompanyDetails.jsx to reduce file size and improve maintainability.
 * Manages all click-outside event handlers for closing various menus:
 * - Financial filters (consolidated/individual)
 * - Similar companies sort menu
 * - Similar companies view menu
 * - Heatmap tile size menu
 * - Heatmap color coding menu
 * - Financial download popup
 *
 * @module useClickOutsideHandlers
 */

import { useEffect } from 'react';

/**
 * Hook for managing click-outside event handlers
 *
 * @param {Object} deps - Dependencies
 * @param {Object} deps.financialsState - Financials state object
 * @param {Object} deps.similarCompaniesState - Similar companies state object
 */
const useClickOutsideHandlers = ({ financialsState, similarCompaniesState }) => {
  // Financial filters click outside (consolidated)
  useEffect(() => {
    const onGlobalClick = (event) => {
      // Handle consolidated filters
      (() => {
        const foundOpenedFilter = Object.entries(
          financialsState.toggledFinancialFiltersConsolidated
        ).find(([_, isToggled]) => isToggled);
        if (!foundOpenedFilter) {
          return;
        }

        const isClickInside = event.target.closest('.lvl-filter-popup');
        if (isClickInside) {
          return;
        }

        const groupId = foundOpenedFilter[0];
        financialsState.toggleFinancialFilterConsolidated(groupId);
      })();

      // Handle individual filters
      (() => {
        const foundOpenedFilter = Object.entries(
          financialsState.toggledFinancialFiltersIndividual
        ).find(([_, isToggled]) => isToggled);
        if (!foundOpenedFilter) {
          return;
        }

        const isClickInside = event.target.closest('.lvl-filter-popup');
        if (isClickInside) {
          return;
        }

        const groupId = foundOpenedFilter[0];
        financialsState.toggleFinancialFilterIndividual(groupId);
      })();
    };

    document.addEventListener('click', onGlobalClick, { capture: true });

    return () => {
      document.removeEventListener('click', onGlobalClick, { capture: true });
    };
  }, [financialsState]);

  // Similar companies sort menu click outside
  useEffect(() => {
    const handleClickOutsideSort = (event) => {
      if (similarCompaniesState.openedSimilarCompany) {
        return;
      }

      const isClickInsideSortMenu = event.target.closest('.similar-sort-menu-wrap');
      const isClickInsideSortButtonDesktop = event.target.closest('#similar-sort-btn-desktop');
      const isClickInsideSortButtonMobile = event.target.closest('#similar-sort-btn-mobile');

      if (
        isClickInsideSortMenu ||
        isClickInsideSortButtonDesktop ||
        isClickInsideSortButtonMobile
      ) {
        // do nothing
      } else {
        if (similarCompaniesState.isSimilarCompaniesSortToggled) {
          similarCompaniesState.toggleSimilarCompaniesSort();
        }
      }
    };

    document.addEventListener('click', handleClickOutsideSort);

    return () => {
      document.removeEventListener('click', handleClickOutsideSort);
    };
  }, [similarCompaniesState]);

  // Similar companies view menu click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (similarCompaniesState.openedSimilarCompany) {
        return;
      }

      const isClickInsideListSwitchMenu = event.target.closest('.view-menu-wrap');
      const isClickInsideListSwitchButtonDesktop = event.target.closest(
        '#similar-list-change-btn-desktop'
      );
      const isClickInsideListSwitchButtonMobile = event.target.closest(
        '#similar-list-change-btn-mobile'
      );

      if (
        isClickInsideListSwitchMenu ||
        isClickInsideListSwitchButtonDesktop ||
        isClickInsideListSwitchButtonMobile
      ) {
        // do nothing
      } else {
        if (similarCompaniesState.isSimilarCompaniesViewMenuToggled) {
          similarCompaniesState.toggleSimilarCompaniesViewMenu();
        }
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [similarCompaniesState]);

  // Heatmap tile size menu click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideMenu = event.target.closest('.heatmap-control-menu-wrap.tile-size');
      const isClickButton = event.target.closest('#profiler-tile-size-metric');

      if (isClickInsideMenu || isClickButton) {
        // do nothing
      } else {
        if (similarCompaniesState.isProfilerHeatmapTileSizeMenuToggled) {
          similarCompaniesState.toggleProfilerHeatmapTileSizeMenu();
        }
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [similarCompaniesState]);

  // Heatmap color coding menu click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideMenu = event.target.closest('.heatmap-control-menu-wrap.color-coding');
      const isClickButton = event.target.closest('#profiler-color-coding-metric');

      if (isClickInsideMenu || isClickButton) {
        // do nothing
      } else {
        if (similarCompaniesState.isProfilerHeatmapColorCodingMenuToggled) {
          similarCompaniesState.toggleProfilerHeatmapColorCodingMenu();
        }
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [similarCompaniesState]);

  // Financial download popup click outside
  useEffect(() => {
    const onGlobalClick = (event) => {
      if (!financialsState.toggledFinancialDownload) {
        return;
      }

      const isClickInside =
        event.target.closest('.financials-download-popup') || event.target.closest('#psrf-btn');
      if (isClickInside) {
        return;
      }

      if (['download-financials-btn', 'download-onepager-btn'].includes(event.target.id)) {
        return;
      }

      financialsState.toggleFinancialDownload();
    };

    document.addEventListener('click', onGlobalClick, { capture: true });

    return () => {
      document.removeEventListener('click', onGlobalClick, { capture: true });
    };
  }, [financialsState]);
};

export default useClickOutsideHandlers;
